/* iter171 — Employee Messages tab. Lets admins configure clock-in / clock-out
   SMS + in-app reminders. Backend: /admin/employee-reminders (GET/PUT) plus
   /admin/employee-reminders/log diagnostic.

   Defaults: Clock-In 07:59 ET, Clock-Out 17:00 ET, weekdays only, technicians + employees.
   Won't double-send: backend dedups via reminder_log (one row per user/kind/day).
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Bell, Clock, Save, Send, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOW = [
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
];

const EmployeeMessagesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState(null);
  const [recentLog, setRecentLog] = useState([]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/employee-reminders`, { withCredentials: true });
      setS(data);
      const { data: log } = await axios.get(`${API}/admin/employee-reminders/log`, { withCredentials: true });
      setRecentLog(Array.isArray(log) ? log.slice(0, 30) : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reminder settings');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const patch = (key, value) => setS((prev) => ({ ...prev, [key]: value }));
  const toggleDay = (day) => {
    const next = new Set(s.days_of_week || []);
    next.has(day) ? next.delete(day) : next.add(day);
    patch('days_of_week', Array.from(next));
  };

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/employee-reminders`, {
        clockin_enabled: !!s.clockin_enabled,
        clockin_time: s.clockin_time,
        clockin_message: s.clockin_message,
        clockout_enabled: !!s.clockout_enabled,
        clockout_time: s.clockout_time,
        clockout_message: s.clockout_message,
        grace_minutes: Number.isFinite(+s.grace_minutes) ? +s.grace_minutes : 5,
        days_of_week: s.days_of_week || [],
        roles: s.roles || [],
        send_sms: !!s.send_sms,
        send_in_app: !!s.send_in_app,
      }, { withCredentials: true });
      toast.success('Reminder settings saved');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    }
    setSaving(false);
  };

  if (loading || !s) {
    return <div data-testid="emp-msgs-loading" className="text-[#A3A3A3]">Loading reminder settings…</div>;
  }

  return (
    <div data-testid="employee-messages-tab" className="space-y-6">
      <div>
        <h3 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#D4AF37]" /> Clock-In / Clock-Out Reminders
        </h3>
        <p className="text-[#A3A3A3] text-sm mt-1 max-w-3xl">
          Auto-nudge field employees who haven&apos;t clocked in by the start of their shift, and remind them to
          clock out at the end of the day. Reminders honor each employee&apos;s active state and never double-send.
        </p>
      </div>

      {/* Clock-In card */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-white font-bold">Clock-In Reminder</p>
          </div>
          <Switch
            data-testid="reminder-clockin-enabled"
            checked={!!s.clockin_enabled}
            onCheckedChange={(v) => patch('clockin_enabled', v)}
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Time (ET)</Label>
            <Input
              data-testid="reminder-clockin-time"
              type="time"
              value={s.clockin_time || ''}
              onChange={(e) => patch('clockin_time', e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Grace (min)</Label>
            <Input
              data-testid="reminder-grace-minutes"
              type="number"
              min={0}
              max={120}
              value={s.grace_minutes ?? 5}
              onChange={(e) => patch('grace_minutes', e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
            <p className="text-[#737373] text-[11px] mt-1">Waits this long after the shift start before nudging.</p>
          </div>
        </div>
        <p className="text-[#737373] text-[11px]">
          If an employee&apos;s first shift starts later than this time, the nudge is sent at their
          scheduled start (+ grace) instead. Employees on approved full-day leave/holiday are skipped.
        </p>
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Message</Label>
          <Textarea
            data-testid="reminder-clockin-message"
            rows={2}
            value={s.clockin_message || ''}
            onChange={(e) => patch('clockin_message', e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
          />
          <p className="text-[#737373] text-[11px] mt-1">Available variables: <code>{'{first_name}'}</code>, <code>{'{name}'}</code></p>
        </div>
      </div>

      {/* Clock-Out card */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-white font-bold">Clock-Out Reminder</p>
          </div>
          <Switch
            data-testid="reminder-clockout-enabled"
            checked={!!s.clockout_enabled}
            onCheckedChange={(v) => patch('clockout_enabled', v)}
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Time (ET)</Label>
            <Input
              data-testid="reminder-clockout-time"
              type="time"
              value={s.clockout_time || ''}
              onChange={(e) => patch('clockout_time', e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Message</Label>
          <Textarea
            data-testid="reminder-clockout-message"
            rows={2}
            value={s.clockout_message || ''}
            onChange={(e) => patch('clockout_message', e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
          />
        </div>
      </div>

      {/* Days + Roles + Channels */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-5">
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Days of Week</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DOW.map((d) => {
              const active = (s.days_of_week || []).includes(d.key);
              return (
                <button
                  key={d.key}
                  data-testid={`reminder-day-${d.key}`}
                  type="button"
                  onClick={() => toggleDay(d.key)}
                  className={`px-3 py-1.5 rounded border text-xs font-bold transition-colors ${
                    active
                      ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
                      : 'bg-[#0A0A0A] text-white border-[#262626] hover:border-[#525252]'
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Who Gets Reminded</Label>
          <div data-testid="reminder-eligibility-note" className="mt-2 bg-[#0A0A0A] border border-[#262626] rounded p-3">
            <p className="text-white text-sm font-bold">All active time-tracking employees</p>
            <p className="text-[#A3A3A3] text-[11px] mt-1">
              Reminders go to every active employee with time tracking enabled — regardless of role
              (Technician, Service/Field Manager, Office, Admin, Owner). A customer shift on the
              schedule is not required, so office/admin days are covered. Set an employee&apos;s
              <span className="text-white"> Time Tracking</span> to off in their profile to exclude them.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
            <Switch
              data-testid="reminder-send-sms"
              checked={!!s.send_sms}
              onCheckedChange={(v) => patch('send_sms', v)}
            />
            <div>
              <p className="text-white text-sm font-bold flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-[#D4AF37]" /> Send SMS</p>
              <p className="text-[#A3A3A3] text-[11px]">Requires Twilio configured and the employee has a phone on file.</p>
            </div>
          </label>
          <label className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
            <Switch
              data-testid="reminder-send-in-app"
              checked={!!s.send_in_app}
              onCheckedChange={(v) => patch('send_in_app', v)}
            />
            <div>
              <p className="text-white text-sm font-bold flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-[#D4AF37]" /> Send In-App Notification</p>
              <p className="text-[#A3A3A3] text-[11px]">Pushes a notification to the employee&apos;s Nexflow inbox.</p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          data-testid="reminder-save"
          onClick={save}
          disabled={saving}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
        >
          <Save className="w-4 h-4 mr-1.5" /> Save Reminder Settings
        </Button>
      </div>

      {/* Recent log */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-3 flex items-center gap-2">
          <Send className="w-3.5 h-3.5" /> Today&apos;s Reminder Log
        </p>
        <div data-testid="reminder-log-list">
          {recentLog.length === 0 ? (
            <p className="text-[#525252] text-sm italic">No reminders fired yet today.</p>
          ) : (
            <div className="space-y-1 text-xs">
              {recentLog.map((r) => (
                <div key={r.id} data-testid={`reminder-log-${r.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded px-2.5 py-1.5 flex items-center justify-between gap-2">
                  <span className="text-white">
                    <span className="font-bold">{r.user_name || r.user_id}</span>{' '}
                    <span className="text-[#A3A3A3]">· {r.kind}</span>
                    {r.skipped && <span className="text-amber-300 ml-1">· skipped ({r.skipped})</span>}
                    {!r.skipped && r.sent_sms && <span className="text-green-300 ml-1">· SMS</span>}
                    {!r.skipped && r.sent_in_app && <span className="text-blue-300 ml-1">· in-app</span>}
                  </span>
                  <span className="text-[#525252]">{r.sent_at ? new Date(r.sent_at).toLocaleTimeString() : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMessagesTab;
